/**
 * TechnicianPreferencesDebug Component
 * Phase 7: Observability/Debug View for Preference Learning Engine
 * Displays learned preferences for a technician in Analytics → Technicians tab
 */

import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Brain, TrendingUp, TrendingDown, AlertTriangle, Info } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

interface TechnicianPreference {
  id: string;
  dimension_type: string;
  dimension_value: string;
  preference_score: number;
  positive_signals: number;
  negative_signals: number;
  sample_size: number;
  consistency_pct: number;
  last_updated: string;
}

interface TechnicianPreferencesDebugProps {
  technicianId: string;
  shopId: string;
}

const dimensionLabels: Record<string, string> = {
  category: 'Job Category',
  make: 'Vehicle Make',
  year_group: 'Year Group',
  time_of_day: 'Time of Day',
};

const dimensionIcons: Record<string, string> = {
  category: '🔧',
  make: '🚗',
  year_group: '📅',
  time_of_day: '⏰',
};

export function TechnicianPreferencesDebug({ technicianId, shopId }: TechnicianPreferencesDebugProps) {
  const [preferences, setPreferences] = useState<TechnicianPreference[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadPreferences() {
      setIsLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await supabase
          .from('technician_preferences')
          .select('*')
          .eq('shop_id', shopId)
          .eq('tech_id', technicianId)
          .order('preference_score', { ascending: false })
          .limit(10);

        if (fetchError) throw fetchError;

        setPreferences(data || []);
      } catch (err: any) {
        console.error('[PREFS-DEBUG] Error loading preferences:', err);
        setError(err.message || 'Failed to load preferences');
      } finally {
        setIsLoading(false);
      }
    }

    if (technicianId && shopId) {
      loadPreferences();
    }
  }, [technicianId, shopId]);

  const getScoreColor = (score: number): string => {
    if (score >= 70) return 'text-green-600 dark:text-green-400';
    if (score >= 55) return 'text-blue-600 dark:text-blue-400';
    if (score >= 45) return 'text-muted-foreground';
    if (score >= 30) return 'text-orange-600 dark:text-orange-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getScoreBadgeVariant = (score: number): 'default' | 'secondary' | 'destructive' | 'outline' => {
    if (score >= 60) return 'default';
    if (score >= 40) return 'secondary';
    return 'outline';
  };

  const formatDimensionValue = (type: string, value: string): string => {
    // Format time of day values
    if (type === 'time_of_day') {
      const timeLabels: Record<string, string> = {
        MORNING: 'Morning (6AM-12PM)',
        MIDDAY: 'Midday (12PM-3PM)',
        AFTERNOON: 'Afternoon (3PM+)',
      };
      return timeLabels[value] || value;
    }
    
    // Title case for other values
    return value
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  if (isLoading) {
    return (
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5" />
            Learned Preferences
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="mt-6 border-destructive">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Error Loading Preferences
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (preferences.length === 0) {
    return (
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5" />
            Learned Preferences
          </CardTitle>
          <CardDescription>
            Preference learning engine - patterns detected from manual ticket moves
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Info className="h-12 w-12 text-muted-foreground/50 mb-3" />
            <p className="text-sm text-muted-foreground">
              No learned preferences yet
            </p>
            <p className="text-xs text-muted-foreground/70 mt-1">
              Preferences are learned when tickets are manually moved between technicians
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Get top 5 preferences
  const topPreferences = preferences.slice(0, 5);

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Brain className="h-5 w-5" />
          Learned Preferences
        </CardTitle>
        <CardDescription>
          Top patterns detected from manual ticket assignments (last 90 days)
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {topPreferences.map(pref => (
            <div
              key={pref.id}
              className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-xl" role="img" aria-label={pref.dimension_type}>
                  {dimensionIcons[pref.dimension_type]}
                </span>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">
                      {formatDimensionValue(pref.dimension_type, pref.dimension_value)}
                    </span>
                    <Badge variant="outline" className="text-xs">
                      {dimensionLabels[pref.dimension_type]}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <TrendingUp className="h-3 w-3 text-green-500" />
                      {pref.positive_signals} moves to
                    </span>
                    <span className="flex items-center gap-1">
                      <TrendingDown className="h-3 w-3 text-red-500" />
                      {pref.negative_signals} moves away
                    </span>
                    <span>•</span>
                    <span>{pref.sample_size} total observations</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className={cn('text-lg font-bold', getScoreColor(pref.preference_score))}>
                    {pref.preference_score}%
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {pref.consistency_pct}% consistent
                  </div>
                </div>
                <Badge variant={getScoreBadgeVariant(pref.preference_score)}>
                  {pref.preference_score >= 60 ? 'Strong' : pref.preference_score >= 45 ? 'Moderate' : 'Weak'}
                </Badge>
                {pref.sample_size < 5 && (
                  <Badge variant="secondary" className="text-xs">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Low sample
                  </Badge>
                )}
              </div>
            </div>
          ))}
        </div>
        
        {preferences.length > 5 && (
          <p className="text-xs text-muted-foreground text-center mt-4">
            Showing top 5 of {preferences.length} learned preferences
          </p>
        )}
      </CardContent>
    </Card>
  );
}
